import React from "react";
import PropTypes from "prop-types";
import { AccessTime } from "@mui/icons-material";
import {
  Box,
  Button,
  Typography,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material";
import PgHomeSectionPaper from "./PgHomeSectionPaper";
import {
  PG_ACTIVITY_MAX_DESC_LEN,
  getActivityFeedDescription,
  renderHighlightedActivityText,
} from "../homeActivityText";
import { formatRelativeTime, getBondUpdateUrl } from "../../../globals/utils/homeHelpers";

/** Recent PG activity grid panel (summary + expandable rows + pagination). */
export default function PgHomeActivitySection({
  activityCopy,
  activities,
  loadingActivities,
  hasMoreActivities,
  selectedActivity,
  onSelectActivity,
  onLoadMore,
  recentActiveCount,
}) {
  return (
    <PgHomeSectionPaper sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: "#333333" }}>
            {activityCopy.title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
            {activityCopy.subtitle}
          </Typography>
        </Box>
        {loadingActivities && <CircularProgress size={18} />}
      </Box>

      <Box>
        {!selectedActivity ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: 280,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {activities.length === 0 && !loadingActivities ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {activityCopy.emptyActivity}
              </Typography>
            ) : (
              activities.map((item, idx) => (
                <Box
                  key={`${item?.bond_id}-${idx}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (theme) => (theme.palette.mode === "light" ? "#ffffff" : "background.default"),
                    border: "1px solid rgba(37, 99, 235, 0.12)",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "text.primary" }}>
                        {(() => {
                          const text = getActivityFeedDescription(item);
                          const tooLong = text.length > PG_ACTIVITY_MAX_DESC_LEN;
                          const shown = tooLong ? (
                            <>
                              {renderHighlightedActivityText(text.slice(0, PG_ACTIVITY_MAX_DESC_LEN), item)}
                              …
                            </>
                          ) : (
                            renderHighlightedActivityText(text, item)
                          );
                          return (
                            <>
                              {shown}
                              {tooLong && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => onSelectActivity(item)}
                                  sx={{
                                    ml: 0.5,
                                    p: 0,
                                    minWidth: 0,
                                    textTransform: "none",
                                    color: "#2563eb",
                                  }}
                                >
                                  {activityCopy.seeMore}
                                </Button>
                              )}
                            </>
                          );
                        })()}
                      </Typography>
                      <Box mt={0.75} display="flex" alignItems="center" gap={1}>
                        {typeof recentActiveCount === "number" && idx < recentActiveCount ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label="New"
                            sx={{
                              bgcolor: (theme) =>
                                theme.palette.mode === "light"
                                  ? "rgba(16,185,129,0.12)"
                                  : "rgba(16,185,129,0.18)",
                              color: "#0f9f6e",
                              borderColor: "rgba(16,185,129,0.35)",
                              fontWeight: 600,
                              height: 22,
                            }}
                          />
                        ) : null}
                        {item?.lawfirm_name ? (
                          <Chip size="small" variant="outlined" label={item.lawfirm_name} />
                        ) : null}
                        {item?.event_at ? (
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              color: "text.secondary",
                            }}
                          >
                            <AccessTime sx={{ fontSize: 14, color: "text.disabled" }} />
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {formatRelativeTime(item.event_at)}
                            </Typography>
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const url = getBondUpdateUrl(item?.bond_type, item?.bond_id);
                        if (url) window.open(url, "_blank");
                      }}
                      sx={{
                        textTransform: "none",
                        color: "#2563eb",
                        whiteSpace: "nowrap",
                        alignSelf: "flex-end",
                      }}
                    >
                      {activityCopy.viewBond}
                    </Button>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        ) : (
          <Box sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {renderHighlightedActivityText(getActivityFeedDescription(selectedActivity), selectedActivity)}
            </Typography>
            <Box mt={0.75} display="flex" alignItems="center" gap={1}>
              {typeof recentActiveCount === "number" &&
              (() => {
                const i = activities.indexOf(selectedActivity);
                return i > -1 && i < recentActiveCount ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="New"
                    sx={{
                      bgcolor: (theme) =>
                        theme.palette.mode === "light"
                          ? "rgba(16,185,129,0.12)"
                          : "rgba(16,185,129,0.18)",
                      color: "#0f9f6e",
                      borderColor: "rgba(16,185,129,0.35)",
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                ) : null;
              })()}
              {selectedActivity?.lawfirm_name ? (
                <Chip size="small" variant="outlined" label={selectedActivity.lawfirm_name} />
              ) : null}
              {selectedActivity?.event_at ? (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    color: "text.secondary",
                  }}
                >
                  <AccessTime sx={{ fontSize: 14, color: "text.disabled" }} />
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatRelativeTime(selectedActivity.event_at)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
            <Box display="flex" justifyContent="space-between" mt={1.5}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSelectActivity(null)}
                sx={{
                  textTransform: "none",
                  color: "#2563eb",
                  borderColor: "rgba(37, 99, 235, 0.3)",
                }}
              >
                {activityCopy.goBack}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const item = selectedActivity;
                  const url = getBondUpdateUrl(item?.bond_type, item?.bond_id);
                  if (url) window.open(url, "_blank");
                }}
                sx={{ textTransform: "none", color: "#2563eb", whiteSpace: "nowrap" }}
              >
                {activityCopy.viewBond}
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {hasMoreActivities && (
        <>
          <Divider sx={{ my: 1.5, borderColor: "rgba(37, 99, 235, 0.12)" }} />
          <Box display="flex" justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadMore}
              disabled={loadingActivities}
              sx={{
                textTransform: "none",
                color: "#2563eb",
                borderColor: "rgba(37, 99, 235, 0.3)",
              }}
            >
              {activityCopy.loadMoreLabel}
            </Button>
          </Box>
        </>
      )}
    </PgHomeSectionPaper>
  );
}

PgHomeActivitySection.propTypes = {
  activityCopy: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    emptyActivity: PropTypes.string,
    seeMore: PropTypes.string,
    loadMoreLabel: PropTypes.string,
    viewBond: PropTypes.string,
    goBack: PropTypes.string,
  }).isRequired,
  activities: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadingActivities: PropTypes.bool.isRequired,
  hasMoreActivities: PropTypes.bool.isRequired,
  selectedActivity: PropTypes.object,
  onSelectActivity: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  recentActiveCount: PropTypes.number,
};
